/**
 * MapSettingsModal — this galaxy map's look, in two tabs.
 *
 *   Background — the translucent, recolourable vector backdrop behind the grid.
 *   Strokes    — the map-wide default path style every un-styled edge inherits.
 *
 * Opened from the builder's "Map Settings" toolbar button. Both tabs are pure
 * map state: Save reports them together through onConfirm and the caller marks
 * the map dirty, so they persist on the next builder SAVE (exactly how the
 * backdrop alone behaved before this modal grew a second tab).
 *
 * Tabs mirror TrlGameSettingsModal — module-level TABS, one activeTab string,
 * a renderTabContent switch, TabNavigation as the whole body, one Save in the
 * footer committing every tab at once.
 *
 * (Formerly MapStyleModal, which only ever held the backdrop.)
 */

import { useState } from 'react';
import { type Tab } from '@swtorbit/shared-components';
import { Modal, Tabs } from '@swtorbit/ui/react';
import {
  DEFAULT_TRL_EDGE_STYLE,
  type TrlMapBackground,
  type TrlEdgeStyle,
} from '@swtorbit/shared-socket-events';
import { MapBackgroundFields } from './MapBackgroundFields';
import { EdgeStyleFields } from './EdgeStyleFields';

const TABS: Tab[] = [
  { id: 'background', label: 'Background' },
  { id: 'strokes', label: 'Strokes' },
];

/** Both map-level visual settings, applied together on Save. */
export interface MapSettingsResult {
  background: TrlMapBackground | undefined;
  defaultEdgeStyle: TrlEdgeStyle;
}

interface MapSettingsModalProps {
  /** The map's current backdrop, or undefined for no backdrop. */
  background?: TrlMapBackground;
  /** The map's current default path style, or undefined for the built-in look. */
  defaultEdgeStyle?: TrlEdgeStyle;
  onConfirm: (next: MapSettingsResult) => void;
  onCancel: () => void;
}

export function MapSettingsModal({
  background,
  defaultEdgeStyle,
  onConfirm,
  onCancel,
}: MapSettingsModalProps) {
  const [activeTab, setActiveTab] = useState<string>(TABS[0].id);
  const [bg, setBg] = useState<TrlMapBackground | undefined>(background);
  // A map with no authored default renders as the built-in look, so that is
  // what the controls open on — saving then pins it explicitly.
  const [stroke, setStroke] = useState<TrlEdgeStyle>(() => ({
    ...DEFAULT_TRL_EDGE_STYLE,
    ...defaultEdgeStyle,
  }));

  const renderTabContent = () => {
    switch (activeTab) {
      case 'background':
        return <MapBackgroundFields value={bg} onChange={setBg} />;
      case 'strokes':
        return (
          <div className="d-flex flex-column gap-sm">
            <p className="fs-xs text-slate m-0">
              New paths — and any existing path you haven&apos;t styled individually — use these
              settings. Right-click a path to give it its own look, or to reset it back to this
              default.
            </p>
            <EdgeStyleFields value={stroke} onChange={setStroke} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Modal open onClose={onCancel} className="trl-map-settings-modal" title="Map Settings" footer={
<>
        <div className="d-flex gap-xs" style={{ marginLeft: 'auto' }}>
          <button type="button" className="btn btn--outline btn--slate" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--green"
            onClick={() => onConfirm({ background: bg, defaultEdgeStyle: stroke })}
          >
            Save
          </button>
        </div>
      </>
}>
        <Tabs variant="bordered" items={TABS.map((t) => ({ value: t.id, label: t.label }))} value={activeTab} onChange={setActiveTab}>{renderTabContent()}</Tabs>
      </Modal>
  );
}
